import { get, has, set } from 'lodash';
import React, { RefObject, SyntheticEvent } from 'react';
import classnames from 'classnames';
import { isLayerType } from 'common/utils/blocks/is-layer-type';
import { BBCommonProps, BBEditableModeProps, EditorMode } from 'types/story';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import css from 'client/components/common/BuildingBlocks/Fields/TextField/TextField.scss';
import { COMPONENT_STATES } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import {
	FieldBBType,
	FIELD_DATA_ATTR,
	FieldEditMode,
	FIELD_EDITABLE_ATTR,
} from 'client/components/common/BuildingBlocks/Fields/constants';
import { SelectionHintEvent } from 'client/components/common/SelectionHint/SelectionHintEvent';
import { Error } from '../Error';
import { placeholderColorPath, placeholderPath, placeholderShowPath, withField } from '../utils';
import { TFieldProps, TFieldState } from '../types';
import DatePicker from './DatePicker';

type State = TFieldState;

type Props = BBCommonProps & {
	getErrorMsg: (...args: any[]) => any;
};

// fixme: chunk load is failed, when story url contains path to card in published url
//        need to make request always to story root path, but not card directory path
// const DatePicker = React.lazy(() => import('client/components/common/BuildingBlocks/Fields/TextField/DatePicker'));

class TextField extends React.Component<Props, State> {
	state = {
		editMode: FieldEditMode.value,
		value: get(this.props, placeholderPath),
		readOnly: true,
		pending: false, // pending for a value storing
	};

	static getDerivedStateFromProps(props: Props, state: State) {
		const nextState: Partial<State> = {
			editMode: get(props, placeholderShowPath) ? FieldEditMode.placeholder : FieldEditMode.value,
		};

		const placeholder: string | undefined = get(props, placeholderPath);
		if (placeholder === state.value && state.pending) {
			nextState.value = placeholder;
			nextState.readOnly = true;
			nextState.pending = false;
		}

		// in case of value is updated when edited master symbols from other instance
		if (state.readOnly && placeholder !== state.value) {
			nextState.value = placeholder;
		}

		return nextState;
	}

	get path() {
		return get(this.props.editableModeProps, ['nodeProps', 'data-path']);
	}

	getFieldProps() {
		const type = get(this.props, ['uiConfig', 'componentProps', UNIFORM_PROPS.fieldType], FieldBBType.text);
		const { name } = this.props.uiConfig.editorProps;
		const placeholder = get(this.props, ['uiConfig', 'componentProps', UNIFORM_PROPS.fieldPlaceholder]);
		const color = get(this.props, placeholderColorPath);

		const props: TFieldProps = {
			className: css.field,
			type,
			name,
			placeholder,
			autoComplete: 'off',
			[FIELD_DATA_ATTR]: '',
		};

		if (this.props.isEditableMode) {
			props.value = this.state.readOnly ? props.placeholder : this.state.value;

			delete props.placeholder;

			props.readOnly = this.state.readOnly;

			if (this.props.editorMode === EditorMode.CONTENT) {
				props.onClick = this.enableEditing;
			} else {
				props.onDoubleClick = this.enableEditing;
			}

			props.onChange = event => {
				this.setState({ value: event.target.value });
			};

			props.onBlur = this.onEditableBlur;

			if (this.state.editMode === FieldEditMode.placeholder) {
				set(props, `style.color`, color);
			} else {
				set(props, `style.color`, '');
			}

			if (!props.readOnly) {
				props[FIELD_EDITABLE_ATTR] = ''; // needed to control component drag
			}

			props.onFocus = (event: SyntheticEvent) => {
				// prevent this block from being selected when clicking on its parent
				event.stopPropagation();
			};
		}

		return props;
	}

	enableEditing = () => {
		this.setState({ readOnly: false }, () =>
			SelectionHintEvent.triggerFocus(this.props as WithRequired<BBCommonProps, 'editableModeProps'>)
		);
	};

	onEditableBlur = () => {
		if (this.state.value === get(this.props, placeholderPath)) {
			this.setState({ readOnly: true });
			return;
		}

		transmitToAdminBbUpdate({
			id: TextField.name,
			path: `elements.${this.path}.${placeholderPath}`,
			value: this.state.value,
			isStory: isLayerType(this.props).global,
		});

		this.setState({ pending: true });
	};

	renderEditable() {
		const props = { ...this.props.editableModeProps?.nodeProps, className: css.editable };
		return this.renderDefault(props);
	}

	renderDefault(props: Partial<BBEditableModeProps['nodeProps']> = {}) {
		const { nodeProps, editorProps } = this.props.uiConfig;
		const inputProps = this.getFieldProps();
		const className = classnames(css.textField, css[inputProps.type], nodeProps.className, props.className);
		const isIncorrect = has(props, 'data-incorrect-state') || this.props.states[COMPONENT_STATES.INCORRECT];
		const errorMessage = this.props.getErrorMsg(props);

		const common = {
			...this.props.uiConfig.nodeProps,
			...this.props.stateAttrs,
			...this.props.eventListeners,
		};

		return (
			<div
				{...common}
				{...props}
				className={className}
				ref={this.props.containerRef as RefObject<HTMLDivElement>}
			>
				{this.renderInput(inputProps)}
				<Error
					isHidden={!isIncorrect || typeof errorMessage === 'undefined'}
					path={this.path}
					isEditableMode={!!this.props.isEditableMode}
					errorType={editorProps[UNIFORM_PROPS.fieldErrorShow]}
					text={errorMessage}
					editorMode={this.props.editorMode}
				/>
			</div>
		);
	}

	renderInput(inputProps: TFieldProps) {
		switch (inputProps.type) {
			case FieldBBType.date: {
				const input = <input {...inputProps} type="text" />;
				return this.props.isEditableMode ? (
					input
				) : (
					<DatePicker {...inputProps} uiConfig={this.props.uiConfig} />
				);
			}
			default:
				return <input {...inputProps} />;
		}
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

export default withCardTransitionContext(withField(TextField));
